import { JBButton } from '@jbhi-fi/lanyard-ui';
import { DeliveryChoice } from '@jbhi-fi/lanyard-ui/components/DeliveryChoice';
import React from 'react';
import { styles } from './DeliveryContainer.css';
import { LocationInfoHeader } from '../LocationInfoHeader/LocationInfoHeader';
import { DeliveryOption, FulfilmentStore } from '../../../utils/types';
import { UnavailableForDelivery } from '../ErrorSuccessTabs/UnavailableForDelivery';
import { SHOULD_LOCALISATION_SHOW_DELIVERY_MESSAGING, BULKY_DELIVERY_DEFAULT_EXPERIENCE } from '../constants';
import { PremiumDeliveryTile } from '../PremiumDeliveryTile/PremiumDeliveryTile';
import { formattedUberDeliveryOption } from '../../../../availability/utils/availabilityUtils';
import { PremiumDeliveryOptions } from '@jbhi-fi/utils/src/premiumDelivery/premiumDelivery';

export interface DeliveryContainerProps {
  yourSuburb?: string;
  yourStore?: FulfilmentStore;
  deliveryOptions?: Array<DeliveryOption>;
  handleClickViewOtherOptions: () => void;
  isInStoreOnly?: boolean;
  isBackOrder?: boolean;
  isPremiumDeliveryAvailable?: boolean;
  marketplaceSellerName?: string;
  isError?: boolean;
}

const ViewDetailsButton = ({ handleClickViewOtherOptions }: { handleClickViewOtherOptions: () => void }) => (
  <div className={styles.viewDetailsContainer} data-testid="delivery-view-other-options">
    <JBButton type="link" onClick={handleClickViewOtherOptions} data-testid="localisation-delivery-view-options-button">
      View details
    </JBButton>
  </div>
);

export const DeliveryContainer = ({
  yourSuburb,
  deliveryOptions,
  handleClickViewOtherOptions,
  isInStoreOnly,
  isPremiumDeliveryAvailable,
  marketplaceSellerName,
  isError,
}: DeliveryContainerProps) => {
  const isTopContainer = window.Jbhifi?.localisationSettings?.topContainer === 'delivery';
  if (!yourSuburb) return null;
  const uberDeliveryOption = deliveryOptions?.find(opt => opt.serviceImage?.includes('uber'));
  const getDeliveryOptionsToDisplay = () => {
    if (isPremiumDeliveryAvailable) {
      return deliveryOptions?.filter(option => !Object.values(PremiumDeliveryOptions).includes(option.experienceName));
    }
    const priorityDeliveryOptions = deliveryOptions?.slice(0, 2);
    if (uberDeliveryOption) {
      uberDeliveryOption.title = formattedUberDeliveryOption(uberDeliveryOption.title ?? '');
      return [uberDeliveryOption];
    }
    const fastest = priorityDeliveryOptions?.filter(opt => opt.type === 'fastest');
    if (fastest?.length) return fastest.slice(0, 1);
    return priorityDeliveryOptions?.slice(0, 1);
  };
  const isDeliveryAvailable = Boolean(yourSuburb && getDeliveryOptionsToDisplay()?.length);

  const Header = (
    <LocationInfoHeader label="Delivery to:" isDeliveryContainer={true} descriptionFirstLine={yourSuburb} />
  );
  if (isError) {
    return (
      <div className={styles.deliveryContainer({ isTopContainer })} data-testid="localisation-delivery-container">
        <UnavailableForDelivery isError={true}>{Header}</UnavailableForDelivery>
        <ViewDetailsButton handleClickViewOtherOptions={handleClickViewOtherOptions} />
      </div>
    );
  }

  return (
    <div className={styles.deliveryContainer({ isTopContainer })} data-testid="localisation-delivery-container">
      {isInStoreOnly || !isDeliveryAvailable ? (
        <UnavailableForDelivery isInStoreOnly={isInStoreOnly}>{Header}</UnavailableForDelivery>
      ) : yourSuburb && getDeliveryOptionsToDisplay()?.length ? (
        <>
          {Header}
          {getDeliveryOptionsToDisplay()?.map((option, i) => {
            const isLastDeliveryOption = getDeliveryOptionsToDisplay()?.length === i + 1;
            return (
              <div
                key={i}
                className={styles.deliveryOptionContainer({ isLastDeliveryOption })}
                data-testid="localisation-delivery-option"
              >
                <DeliveryChoice
                  deliveryDate={option.title}
                  experienceName={!marketplaceSellerName ? option.experienceName : ''}
                  price={option.price}
                  carrier={{ name: '', imageUrl: option.serviceImage }}
                  tags={[]}
                  shouldShowPrice={false}
                  shouldShowGreenCircle
                />
                {SHOULD_LOCALISATION_SHOW_DELIVERY_MESSAGING && option.messaging ? (
                  <div className={styles.deliveryMessaging} data-testid="localisation-delivery-message">
                    {option.messaging}
                  </div>
                ) : null}
                {marketplaceSellerName ? (
                  <div
                    className={styles.marketplaceSellerCallout}
                    data-testid="marketplace-seller_callout_pdp-availability"
                  >
                    {`This product is sent by ${marketplaceSellerName}`}
                  </div>
                ) : null}
              </div>
            );
          })}
          {isPremiumDeliveryAvailable ? <PremiumDeliveryTile deliveryOptions={deliveryOptions} /> : null}
          <ViewDetailsButton handleClickViewOtherOptions={handleClickViewOtherOptions} />
        </>
      ) : null}
    </div>
  );
};
